import { daysInMonth } from './calendar.js';
// ---------------------------------------------------------------------------
// The five kinds, as the Billing tab offers them (§7.1)
// ---------------------------------------------------------------------------
export const BILLING_KIND_QUESTION = 'How do you bill this?';
export const BILLING_KIND_QUESTION_SUB = 'pick one — each has its own sentence below';
export const BILLING_KIND_CARDS = {
    completion: { title: 'When it’s done', sentence: 'An invoice for each visit as it is finished', icon: 'check' },
    period: { title: 'Same amount each period', sentence: 'Weekly or monthly, on a schedule you set', icon: 'repeat' },
    milestones: { title: 'In stages', sentence: 'A share at each step you name', icon: 'layers' },
    deposit_balance: { title: 'Deposit, then the rest', sentence: 'Money up front, the balance later', icon: 'dollar' },
    time_materials: { title: 'Time & materials', sentence: 'What was used, up to a ceiling', icon: 'clock' },
};
// ---------------------------------------------------------------------------
// Terms (§7.4)
// ---------------------------------------------------------------------------
/** The `Net N` rows of the Terms list, in order. */
export const TERMS_NET_DAYS = [7, 10, 15, 30, 45, 60];
export const TERMS_MAX_DAYS = 180;
export const TERMS_WORDS = {
    customer: 'Same as the customer',
    customerHelp: (days) => `${termsLabel(days)} today — read from the account`,
    onReceipt: 'On receipt',
    custom: 'Custom…',
    customHelp: 'any number of days, 0 to 180',
    sheetTitle: 'Terms',
    sheetSub: 'Due this many days after an invoice is sent',
    label: 'Terms',
};
/** `On receipt` for nought, `Net 15` otherwise — the plan header's label. */
export function termsLabel(days) {
    return days === 0 ? 'On receipt' : `Net ${days}`;
}
/**
 * The one terms fact the proposal states, from the plans the customer has
 * chosen something on: their **effective** terms, if they all agree. Two
 * different answers say nothing — the invoice itself will — because a guess
 * on a page somebody signs is worse than silence.
 */
export function invoiceTermsOf(plans) {
    if (plans.length === 0)
        return null;
    const days = new Set(plans.map((plan) => plan.days));
    if (days.size !== 1)
        return null;
    return { days: plans[0].days, onAcceptFirst: plans.some((plan) => plan.onAccept) };
}
/** §9.4's sentences, literal. */
export function invoiceTermsSentence(terms) {
    const head = terms.days === 0
        ? 'Invoices are due when you receive them.'
        : `Invoices are due ${terms.days} ${terms.days === 1 ? 'day' : 'days'} after they are sent.`;
    return terms.onAcceptFirst && terms.days > 0
        ? `${head} The first payment is due when you accept.`
        : head;
}
// ---------------------------------------------------------------------------
// planProposalText — the fragment after the price
// ---------------------------------------------------------------------------
/**
 * What the customer reads after the figure: `for 7 months`, `2 visits,
 * billed as each is done`, `in 3 payments: 30%, 40%, 30%`. The plan card's
 * own words are `planHeadline`/`planReads`; this one is the customer's.
 */
export function planProposalText(plan, ctx = {}) {
    switch (plan.kind) {
        case 'deposit_balance': {
            const deposit = plan.depositFlat !== null && plan.depositFlat !== undefined
                ? usd(plan.depositFlat)
                : `${depositPctSaid(plan.depositPct)}%`;
            return plan.restMode === 'spread'
                ? `${deposit} when you accept, the rest as each visit is done`
                : `${deposit} when you accept, the rest when it is finished`;
        }
        case 'milestones':
            return stagesText(plan.milestones);
        case 'time_materials':
            /* The cap is its own line on the page (board 12 row 6), drawn from
             * `plan.cap`, so the sentence here is only how the money is worked out. */
            return 'billed on the hours and materials used';
        case 'period': {
            const prorate = prorateTerms(plan);
            if (prorate) {
                return `for ${countSaid(prorate.periods)} ${prorate.periods === 1 ? 'month' : 'months'}, part months prorated`;
            }
            return periodText(plan, ctx.periods ?? null);
        }
        case 'completion':
        default:
            if (plan.kind === 'completion' && plan.completionMode === 'last') {
                return 'billed once, when the work is done';
            }
            return completionText(ctx.occurrences ?? 1);
    }
}
/**
 * A period plan in words, and `period_every` is why this is not one line.
 *
 * "for 3 months" is the easy case. Every other `period_every` needs a
 * different sentence, because the plural of the *unit* is not the plural of
 * the *period*: a plan billing every two weeks, three times, is not "for 3
 * weeks" — so anything but one-per-unit counts the payments and says the
 * cadence separately.
 */
function periodText(plan, periods) {
    const unit = plan.periodUnit;
    const every = Math.max(1, plan.periodEvery);
    const cadence = every === 1 ? unit : `${every} ${unit}s`;
    if (plan.periodBasis === 'actual') {
        return every === 1
            ? `billed for what each ${unit} held`
            : `billed every ${cadence}, for what that period held`;
    }
    if (periods === null)
        return `billed every ${cadence} while it runs`;
    if (every === 1)
        return `for ${periods} ${periods === 1 ? unit : `${unit}s`}`;
    return `for ${periods} ${periods === 1 ? 'payment' : 'payments'}, one every ${cadence}`;
}
/**
 * The fragment for work on no plan at all — the proposal's fallback, the
 * same words a completion plan says.
 */
export function completionFragment(occurrences) {
    return completionText(occurrences);
}
/**
 * Board 12 row 3 and board 4's optional visit, word for word: "9 visits,
 * billed as each is done" where there are several, "billed when it is done"
 * where there is one.
 */
function completionText(occurrences) {
    return occurrences > 1
        ? `${occurrences} visits, billed as each is done`
        : 'billed when it is done';
}
/**
 * The stages, for the customer: the shares where every stage is a share
 * (today's sentence), the amounts where every stage is an amount, and a blend
 * as `$2,000.00, then 50% and 50% of what is left`.
 */
function stagesText(stages) {
    const count = stages.length;
    if (count === 0)
        return '';
    const flats = stages.filter((one) => one.amount !== null);
    const shares = stages.filter((one) => one.amount === null && one.pct !== null);
    if (flats.length === 0) {
        return shares.length === count
            ? `in ${count} payments: ${shares.map((one) => `${formatPct(one.pct)}%`).join(', ')}`
            : `in ${count} payments`;
    }
    const amounts = flats.map((one) => usd(one.amount)).join(', ');
    if (shares.length === 0)
        return `in ${count} payments: ${amounts}`;
    return `in ${count} payments: ${amounts}, then ${restSaid(shares)}`;
}
/** `50% and 50% of what is left` · `then the rest` for one stage at 100. */
function restSaid(shares) {
    if (shares.length === 1 && shares[0].pct === 100)
        return 'the rest';
    return `${andList(shares.map((one) => `${formatPct(one.pct)}%`))} of what is left`;
}
// ---------------------------------------------------------------------------
// planProposalLine — "On the proposal:", the figure and the fragment
// ---------------------------------------------------------------------------
/**
 * The second line of the Reads band: the figure the price block draws, then
 * the fragment, as one sentence in curly quotes —
 * `“$321.43 / month, for 7 months.”`.
 */
export function planProposalLine(plan, ctx = {}) {
    const fragment = planProposalText(plan, ctx);
    if (plan.kind === 'period' && plan.periodBasis === 'even') {
        const prorate = prorateTerms(plan);
        const figure = prorate ? prorate.each : plan.invoices[0]?.amount ?? null;
        if (figure !== null)
            return quoted(`${usd(figure)} / ${cadenceUnit(plan)}, ${fragment}`);
    }
    if (plan.kind === 'time_materials' && plan.capAmount !== null) {
        return quoted(`${capitalise(fragment)}. ${plan.name} will not exceed ${usd(plan.capAmount)}`);
    }
    return quoted(capitalise(fragment));
}
// ---------------------------------------------------------------------------
// planHeadline / planReads — the builder's own words
// ---------------------------------------------------------------------------
/**
 * A package plan's line on **Billing in this package** (visits §10.2, Board 5
 * callout 5): "The same amount each month, billed in advance, due Net 15."
 * A package has no dates and no money yet, so this says the rule — never a
 * figure, never a month. Board 5 draws the period sentence; the other kinds
 * are said the same way (step 18 DEVIATION REQUEST 3).
 */
export function packagePlanSentence(plan) {
    const due = plan.termsMode === 'customer' ? 'due on the customer’s terms'
        : plan.termsDays === 0 ? 'due on receipt' : `due ${termsLabel(plan.termsDays)}`;
    const unit = plan.periodEvery === 1 ? plan.periodUnit : `${plan.periodEvery} ${plan.periodUnit}s`;
    const stages = plan.milestones.length;
    const body = (() => {
        switch (plan.kind) {
            case 'period':
                return plan.periodBasis === 'actual'
                    ? `What each ${unit} held, billed in arrears`
                    : `The same amount each ${unit}, billed in advance`;
            case 'completion':
                return plan.completionMode === 'last'
                    ? 'One invoice when the last visit is finished'
                    : 'An invoice for each visit as it is finished';
            case 'milestones':
                return stages === 0 ? 'In stages, none named yet' : `In ${stages} stage${stages === 1 ? '' : 's'}`;
            case 'deposit_balance':
                return `${plan.depositFlat !== null ? usd(plan.depositFlat) : `${plan.depositPct ?? 50}%`} on approval, then the rest`;
            default:
                return plan.capAmount !== null ? `What was used, never more than ${usd(plan.capAmount)}` : 'What was used';
        }
    })();
    return `${body}, ${due}.`;
}
/** The grey one-liner under a plan's name. Builder only. */
export function planHeadline(plan, ctx = {}) {
    const title = kindTitle(plan);
    switch (plan.kind) {
        case 'completion':
            return plan.completionMode === 'last'
                ? `${title} · one invoice, when the last visit is finished`
                : `${title} · one invoice for each visit, as it is finished`;
        case 'period': {
            const prorate = prorateTerms(plan);
            const window = windowSaid(plan.window);
            if (plan.periodBasis === 'actual') {
                return join(title, ['in arrears', `for what each ${plan.periodUnit} held`, window]);
            }
            if (prorate) {
                return join(title, [spaced(usd(prorate.each), dayWords(plan)), 'in advance', 'part months prorated']);
            }
            const figure = plan.invoices[0]?.amount;
            return join(title, [
                figure === undefined ? '' : spaced(usd(figure), dayWords(plan)), 'in advance', window,
            ]);
        }
        case 'milestones':
            return plan.milestones.length === 0
                ? title
                : `${title} · ${stageWords(plan, ctx).join(' · ')}`;
        case 'deposit_balance':
            return plan.restMode === 'spread'
                ? `${title} · ${depositSaid(plan)} on approval, the balance across the visits`
                : `${title} · ${depositSaid(plan)} on approval, the balance later`;
        case 'time_materials':
            return plan.capAmount !== null
                ? `${title} · what was used, up to ${usd(plan.capAmount)}`
                : `${title} · what was used`;
    }
    return title;
}
/** The green Reads line — the plan, as a sentence. Builder only. */
export function planReads(plan, ctx = {}) {
    switch (plan.kind) {
        case 'completion':
            return plan.completionMode === 'last'
                ? 'One invoice, when the last visit is done.'
                : 'One invoice for each visit, when it is done.';
        case 'period': {
            const window = windowSaid(plan.window);
            const tail = window ? ` — ${window}.` : '.';
            if (plan.periodBasis === 'actual') {
                const day = dayWords(plan);
                return `What each ${plan.periodUnit} actually held, invoiced ${day || 'when it ends'}${tail}`;
            }
            const prorate = prorateTerms(plan);
            if (prorate) {
                return `${usd(prorate.each)} a ${plan.periodUnit}, in advance — the first part-month prorated, the last takes the rest.`;
            }
            const figure = plan.invoices[0]?.amount;
            if (figure === undefined)
                return `Nothing to invoice yet${tail}`;
            return `${spaced(usd(figure), eachPeriodWords(plan))}, in advance${tail}`;
        }
        case 'milestones':
            return plan.milestones.length === 0 ? '' : `${stageWords(plan, ctx).join(' · ')}.`;
        case 'deposit_balance': {
            const deposit = depositSaid(plan);
            if (plan.restMode === 'spread')
                return `${deposit} on approval; the rest as each visit is done.`;
            if (plan.depositFlat !== null && plan.depositFlat !== undefined) {
                const balance = plan.invoices.slice(1).reduce((sum, row) => sum + toCents(row.amount), 0);
                return `${deposit} on approval; the balance of ${usd(balance / 100)} when ${lastVisitSaid(plan, ctx)} is done.`;
            }
            return `${deposit} on approval, the balance when the whole job is done.`;
        }
        case 'time_materials': {
            const when = plan.tmTrigger === 'job_completion'
                ? 'in one invoice when the job is closed out'
                : plan.tmTrigger === 'period' ? 'invoiced every period' : 'invoiced after each visit';
            const cap = plan.capAmount !== null ? ` Not to exceed ${usd(plan.capAmount)}.` : '';
            return `What was used, ${when}.${cap}`;
        }
    }
    return '';
}
/**
 * The nine states of §8.2, literal. `ok` is green, `short` and `over` amber,
 * `exceeds` red — and only `exceeds` is refused by the server; the others
 * feed Before you send's "Payments add up to the visits".
 */
export function stageSumChip(plan) {
    const sum = plan.stageSum;
    if (!sum || sum.flatStages + sum.pctStages === 0)
        return { state: 'empty', text: '' };
    const total = toCents(sum.total);
    const flat = toCents(sum.flat);
    if (sum.flatStages > 0 && flat > total) {
        return {
            state: 'exceeds',
            text: `Flat amounts come to ${usd(sum.flat)} — ${usd((flat - total) / 100)} more than the job`,
        };
    }
    if (sum.pctStages === 0) {
        return flat === total
            ? { state: 'ok', text: `${usd(sum.flat)} of ${usd(sum.total)} ✓` }
            : {
                state: 'short',
                text: `${usd(sum.flat)} of ${usd(sum.total)} — ${usd((total - flat) / 100)} is not on any stage`,
            };
    }
    const pct = Math.round(sum.pct * 100) / 100;
    const head = sum.flatStages > 0
        ? `${usd(sum.flat)} + ${formatPct(pct)}% of the other ${usd(sum.rest)}`
        : `${formatPct(pct)}%`;
    if (pct === 100) {
        return { state: 'ok', text: sum.flatStages > 0 ? `${head} ✓` : `100% of ${usd(sum.total)} ✓` };
    }
    return pct < 100
        ? { state: 'short', text: `${head} — ${formatPct(round2(100 - pct))}% is not on any stage` }
        : { state: 'over', text: `${head} — ${formatPct(round2(pct - 100))}% is over` };
}
/**
 * A stages plan's arithmetic (§7.3), for the server to put on the plan view.
 * Pure; the browser reads the result rather than running it.
 */
export function stageSumOf(plan) {
    const flats = plan.milestones.filter((one) => one.amount !== null);
    const shares = plan.milestones.filter((one) => one.amount === null && one.pct !== null);
    const flat = flats.reduce((sum, one) => sum + toCents(one.amount), 0);
    return {
        total: plan.total,
        flat: flat / 100,
        rest: (toCents(plan.total) - flat) / 100,
        pct: Math.round(shares.reduce((sum, one) => sum + one.pct, 0) * 10_000) / 10_000,
        flatStages: flats.length,
        pctStages: shares.length,
    };
}
// ---------------------------------------------------------------------------
// Prorating (§7.3, §7.6)
// ---------------------------------------------------------------------------
/**
 * **P and A** for a prorating plan: P is the whole months from the window's
 * start to the day after its end, plus the leftover days over the days in the
 * next month; A is the plan total ÷ P, to the cent. The first invoice is A
 * for the days it covers, the middle ones are A, and the last takes the rest.
 *
 * Null unless the plan prorates by the month and spreads evenly — the
 * Part periods row exists only for months, and a plan billing what each
 * month held has nothing to prorate.
 */
export function prorateTerms(plan) {
    if (plan.kind !== 'period' || plan.partPeriod !== 'prorate'
        || plan.periodUnit !== 'month' || plan.periodBasis !== 'even' || !plan.window)
        return null;
    const periods = prorationPeriods(plan.window.from, plan.window.to);
    if (periods <= 0)
        return null;
    return { periods, each: Math.round(toCents(plan.total) / periods) / 100 };
}
/**
 * The day a by-period plan bills on (Aaron, review item 5.2): the day of the
 * month or of the week it names — and, where it names none, the day its
 * control already shows, **the 1st** or **Monday**.
 *
 * A plan made from **New billing plan ▾** had no `day_of_month`, so the box
 * read `1st` while the planner dated every invoice on the day the visits
 * started — Oct 13, Nov 13, … — and the Reads band said "on the 13th" beside
 * a box saying the 1st. The control, the planner and the route that makes a
 * plan all read this one answer now, so a plan cannot show one day and bill
 * on another. Null only for a plan billed by the day, which has no day to
 * name.
 */
export const DEFAULT_BILLING_DAY = { month: 1, week: 1 };
export function billingDayOf(plan) {
    if (plan.periodUnit === 'month')
        return plan.dayOfMonth ?? DEFAULT_BILLING_DAY.month;
    if (plan.periodUnit === 'week')
        return plan.dayOfWeek ?? DEFAULT_BILLING_DAY.week;
    return null;
}
/** P, to four places: whole months, then the leftover days of the next. */
export function prorationPeriods(from, to) {
    const end = addDays(to, 1);
    let months = 0;
    while (addMonthsClamped(from, months + 1) <= end)
        months += 1;
    const after = addMonthsClamped(from, months);
    const leftover = daysBetween(after, end);
    return Math.round((months + leftover / daysInMonthOf(after)) * 10_000) / 10_000;
}
/** The days in the month `iso` falls in. */
export function daysInMonthOf(iso) {
    const [year, month] = iso.split('-').map(Number);
    return daysInMonth(year, month);
}
function addMonthsClamped(iso, months) {
    const [year, month, day] = iso.split('-').map(Number);
    const target = new Date(Date.UTC(year, month - 1 + months, 1));
    const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
    target.setUTCDate(Math.min(day, last));
    return target.toISOString().slice(0, 10);
}
function addDays(iso, days) {
    return new Date(Date.parse(`${iso}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);
}
function daysBetween(from, to) {
    return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}
// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------
/** `$2,000.00` — every billing word carries its cents (§7.6). */
export function usd(value) {
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function kindTitle(plan) {
    if (plan.kind !== 'period')
        return BILLING_KIND_CARDS[plan.kind].title;
    const every = Math.max(1, plan.periodEvery);
    return every === 1
        ? `Same amount each ${plan.periodUnit}`
        : `Same amount every ${every} ${plan.periodUnit}s`;
}
/** The stage words of the headline and Reads: `40% when Tear-off is done`. */
function stageWords(plan, ctx) {
    const blend = plan.milestones.some((one) => one.amount !== null);
    return plan.milestones.map((one) => {
        const how = one.amount !== null
            ? usd(one.amount)
            : `${formatPct(one.pct ?? 0)}%${blend ? ' of the rest' : ''}`;
        return `${how} ${stageWhen(one, ctx)}`;
    });
}
function stageWhen(stage, ctx) {
    switch (stage.trigger) {
        case 'on_approval': return 'on approval';
        case 'on_date': return stage.dueOn ? `on ${monDay(stage.dueOn)}` : 'on a date';
        case 'visit_completed': {
            const visit = stage.visitId ? ctx.visits?.find((one) => one.id === stage.visitId) : undefined;
            return visit ? `when ${visit.name} is done` : 'when a visit is done';
        }
        case 'job_completed': return 'when the job is done';
        case 'progress': return `at ${formatPct(stage.progressPct ?? 0)}% complete`;
    }
    return '';
}
function depositSaid(plan) {
    return plan.depositFlat !== null && plan.depositFlat !== undefined
        ? usd(plan.depositFlat)
        : `${depositPctSaid(plan.depositPct)}%`;
}
function depositPctSaid(pct) {
    const value = pct ?? 50;
    return value % 1 === 0 ? String(value) : value.toFixed(1);
}
/** `the repair` — the plan's last visit, lower-cased, with *the*. */
function lastVisitSaid(plan, ctx) {
    const id = plan.visitIds[plan.visitIds.length - 1];
    const visit = id ? ctx.visits?.find((one) => one.id === id) : undefined;
    return visit ? `the ${visit.name.toLowerCase()}` : 'the work';
}
/** `on the 1st` · `on Fridays` · nothing, where the plan did not say. */
function dayWords(plan) {
    if (plan.periodUnit === 'month') {
        const day = plan.dayOfMonth ?? dayOfFirstInvoice(plan);
        return day ? `on the ${ordinal(day)}` : '';
    }
    if (plan.periodUnit === 'week' && plan.dayOfWeek !== null)
        return `on ${WEEKDAYS[plan.dayOfWeek]}s`;
    return '';
}
/** `on the 1st of each month` · `on Fridays, each week` · `each day`. */
function eachPeriodWords(plan) {
    const every = Math.max(1, plan.periodEvery);
    const each = every === 1 ? `each ${plan.periodUnit}` : `every ${every} ${plan.periodUnit}s`;
    const day = dayWords(plan);
    if (!day)
        return each;
    return plan.periodUnit === 'month' ? `${day} of ${each}` : `${day}, ${each}`;
}
function dayOfFirstInvoice(plan) {
    const on = plan.invoices.find((row) => row.on)?.on;
    return on ? Number(on.slice(8, 10)) : null;
}
/** `month` · `2 weeks` — the unit after the figure's slash. */
function cadenceUnit(plan) {
    const every = Math.max(1, plan.periodEvery);
    return every === 1 ? plan.periodUnit : `${every} ${plan.periodUnit}s`;
}
/** `April to October` for whole months, `Apr 6 – Jul 5` otherwise. */
export function windowSaid(window) {
    if (!window)
        return '';
    const whole = window.from.endsWith('-01') && Number(window.to.slice(8, 10)) === daysInMonthOf(window.to);
    if (!whole)
        return `${monDay(window.from)} – ${monDay(window.to)}`;
    const first = monthName(window.from);
    const last = monthName(window.to);
    return window.from.slice(0, 7) === window.to.slice(0, 7) ? first : `${first} to ${last}`;
}
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
function monthName(iso) {
    return new Date(`${iso.slice(0, 10)}T00:00:00Z`)
        .toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
}
function monDay(iso) {
    return new Date(`${iso.slice(0, 10)}T00:00:00Z`)
        .toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}
function ordinal(n) {
    const suffix = n % 100 >= 11 && n % 100 <= 13 ? 'th'
        : n % 10 === 1 ? 'st' : n % 10 === 2 ? 'nd' : n % 10 === 3 ? 'rd' : 'th';
    return `${n}${suffix}`;
}
function formatPct(value) {
    return value % 1 === 0 ? String(value) : value.toFixed(1);
}
/** A count that may be a part: `3`, `2.8`. */
function countSaid(value) {
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
function andList(parts) {
    if (parts.length <= 1)
        return parts.join('');
    return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}
function join(title, parts) {
    const said = parts.filter(Boolean);
    return said.length > 0 ? `${title} · ${said.join(', ')}` : title;
}
function spaced(...parts) {
    return parts.filter(Boolean).join(' ');
}
function capitalise(text) {
    return text.length > 0 ? `${text[0].toUpperCase()}${text.slice(1)}` : text;
}
function quoted(text) {
    return `“${text}.”`;
}
function toCents(value) {
    return Math.round(value * 100);
}
function round2(value) {
    return Math.round(value * 100) / 100;
}
export const INVOICES_ADD_UP = 'Adds up to the visits ✓';
/**
 * Every row the planner wrote, in Board 12's words. Nothing is folded:
 * Board 11 draws `+ 4 more, July to October`, and AGENTS.md's billing-plans
 * row says nothing in the projection is ever folded into an "N more" row,
 * so every invoice is listed.
 */
export function invoiceTable(plan, ctx = {}) {
    const own = plan.invoices.map((row) => invoiceRow(plan, row, ctx));
    const added = (plan.ifAdded?.invoices ?? []).map((row) => invoiceRow(plan, row, ctx));
    const estimate = plan.invoices.some((row) => row.estimate);
    const cents = plan.invoices.reduce((sum, row) => sum + toCents(row.amount), 0);
    let total = usd(plan.total);
    if (plan.kind === 'time_materials' && plan.capAmount !== null) {
        total = `${total} · not to exceed ${usd(plan.capAmount)}`;
    }
    if (plan.ifAdded)
        total = `${total} · ${usd(plan.ifAdded.total)} ${ifAddedSaid(plan.ifAdded.names)}`;
    return {
        rows: [...own, ...added],
        totalLabel: estimate ? 'Estimated' : 'Total',
        total,
        addsUp: !estimate && plan.invoices.length > 0 && cents === toCents(plan.total),
    };
}
/** `if the fall cleanup is added` · `if the 2 optional visits are added`. */
function ifAddedSaid(names) {
    return names.length === 1
        ? `if the ${names[0].toLowerCase()} is added`
        : `if the ${names.length} optional visits are added`;
}
function invoiceRow(plan, row, ctx) {
    const dated = row.on ? monDay(row.on) : null;
    const visitName = lastVisitName(plan, ctx);
    let invoiced = dated ?? row.whenLabel;
    let tone = dated ? 'date' : 'event';
    let forText = row.description;
    switch (row.role) {
        case 'deposit':
            invoiced = 'On approval';
            tone = 'approval';
            forText = plan.depositFlat !== null && plan.depositFlat !== undefined
                ? 'Deposit · flat'
                : `Deposit · ${depositPctSaid(plan.depositPct)}%`;
            break;
        case 'balance':
            invoiced = dated ?? (plan.visitIds.length === 1 && visitName ? `${visitName} done` : 'Job done');
            break;
        case 'spread':
            invoiced = dated ?? `${row.description} done`;
            break;
        case 'once':
            invoiced = dated ?? 'Job done';
            break;
        case 'stage': {
            const stage = plan.milestones[row.stage ?? -1];
            if (stage) {
                const blend = plan.milestones.some((one) => one.amount !== null);
                forText = `${stage.name} · ${stage.amount !== null
                    ? 'flat'
                    : `${formatPct(stage.pct ?? 0)}%${blend ? ' of the rest' : ''}`}`;
                if (!dated) {
                    if (stage.trigger === 'on_approval') {
                        invoiced = 'On approval';
                        tone = 'approval';
                    }
                    else if (stage.trigger === 'visit_completed') {
                        const visit = stage.visitId ? ctx.visits?.find((one) => one.id === stage.visitId) : undefined;
                        invoiced = visit ? `${visit.name} done` : 'Visit done';
                    }
                    else if (stage.trigger === 'job_completed')
                        invoiced = 'Job done';
                    else if (stage.trigger === 'progress')
                        invoiced = `${formatPct(stage.progressPct ?? 0)}% done`;
                    else
                        invoiced = 'On a date';
                }
            }
            break;
        }
        case 'prorate':
            forText = prorateFor(plan, row) ?? row.description;
            break;
        case 'tm_job':
            invoiced = 'Job closed';
            tone = 'closed';
            forText = plan.visitIds.length === 1 && visitName ? `${visitName} — what was used` : 'What was used';
            break;
        case 'visit':
            // An undated visit that is one trip is named by what it waits for; a run keeps `Visit 3`.
            if (!dated && plan.invoices.filter((one) => one.description === row.description).length <= 1) {
                invoiced = `${row.description} done`;
            }
            if (plan.kind === 'time_materials')
                forText = `${row.description} — what was used`;
            else if (row.optional)
                forText = `${row.description} — optional`;
            break;
        case 'period':
            if (plan.kind === 'time_materials')
                forText = `${row.description} — what was used`;
            break;
    }
    return {
        invoiced,
        tone,
        for: forText,
        amount: row.estimate ? `est. ${usd(row.amount)}` : usd(row.amount),
        due: row.dueLabel ?? '',
        optional: row.optional ?? false,
    };
}
/** `Apr 6–30 · 25 of 30 days` on the first part month, `Jul 1–5 · the rest` on the last. */
function prorateFor(plan, row) {
    const window = plan.window;
    if (!window || !row.on)
        return null;
    const month = row.on.slice(0, 7);
    const short = (iso) => new Date(`${iso.slice(0, 10)}T00:00:00Z`)
        .toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
    if (month === window.from.slice(0, 7) && !window.from.endsWith('-01')) {
        const from = Number(window.from.slice(8, 10));
        const days = daysInMonthOf(window.from);
        const to = month === window.to.slice(0, 7) ? Number(window.to.slice(8, 10)) : days;
        return `${short(window.from)} ${from}–${to} · ${to - from + 1} of ${days} days`;
    }
    const lastDay = Number(window.to.slice(8, 10));
    if (month === window.to.slice(0, 7) && lastDay !== daysInMonthOf(window.to)) {
        return `${short(window.to)} 1–${lastDay} · the rest`;
    }
    return null;
}
/** The plan's last visit's own name — `Repair` — for `Repair done`. */
function lastVisitName(plan, ctx) {
    const id = plan.visitIds[plan.visitIds.length - 1];
    return (id ? ctx.visits?.find((one) => one.id === id)?.name : undefined) ?? null;
}
// ---------------------------------------------------------------------------
// The Billing tab's own words (§8.1–§8.5, Boards 11, 12, 15, 16) — literal
// ---------------------------------------------------------------------------
export const BILLING_TAB = {
    visitsChip: (count) => (count === 1 ? '1 visit' : `${count} visits`),
    visitsOnPlan: 'Visits on this plan',
    addVisit: '+ Add a visit',
    needsPlan: (count) => `${count} needs a plan`,
    invoicesHeading: 'Invoices this will create',
    columns: ['Invoiced', 'For', 'Amount', 'Due'],
    newPlan: '+ New billing plan',
    orDrop: '· or drop a visit here',
    rename: 'Rename',
    delete: 'Delete',
    reads: 'Reads:',
    onTheProposal: 'On the proposal:',
    nextInvoice: (when) => `Next invoice ${when}`,
    dueEvery: (days) => (days === 0 ? 'Due on receipt.' : `Due ${days} days after each invoice is sent.`),
    done: 'Done',
    stageCard: (index) => `Stage ${index}`,
    name: 'Name',
    howMuch: 'How much',
    comesTo: 'Comes to',
    whichVisit: 'Which visit',
    cashTimeline: 'Cash timeline',
    cashCaption: 'Assumes the quote is approved this month and every visit happens when it is scheduled. '
        + 'Shown by the month each invoice is ',
    cashIssued: 'issued',
    defaults: 'Defaults',
    barLabel: 'Billed over the season',
    barSub: (count, first) => `${count === 1 ? '1 invoice' : `${count} invoices`}${first ? ` · first ${first}` : ''}`,
    allClear: 'All clear',
};
export const BILLING_ROWS = {
    invoice: 'Invoice',
    completion: {
        each: 'each visit, as it is finished',
        last: 'once, when the last visit is finished',
        eachHint: 'One invoice for every visit that happens. A visit that is skipped is simply not billed.',
        lastHint: 'Everything on this plan in a single invoice, the day the last visit is finished.',
    },
    every: 'Every',
    onThe: 'on the',
    on: 'on',
    onTheDay: (day) => `${ordinal(day)} of the month`,
    units: { day: 'day', week: 'week', month: 'month' },
    for: 'For',
    payments: 'payments',
    visitsRun: 'the months the visits run',
    theRental: 'the rental',
    changeMonths: 'Change months',
    billed: 'Billed',
    inAdvance: 'In advance',
    inArrears: 'In arrears',
    advanceHint: 'The same amount at the start of each period, whether or not a visit happened.',
    arrearsHint: 'At the end of each period, for what that period actually held. A skipped visit is not on it.',
    partPeriods: 'Part periods',
    fullPeriod: 'Bill a full period',
    prorate: 'Prorate by days',
    fullHint: 'Only matters when the first or last period is part of a month. Choose Prorate by days for rentals.',
    stageColumns: ['Stage', 'How much', 'Comes to', 'When', 'Which one'],
    stageWhen: [
        ['on_approval', 'On approval'],
        ['on_date', 'On a date'],
        ['visit_completed', 'When a visit is done'],
        ['job_completed', 'When the job is done'],
        ['progress', 'At % complete'],
    ],
    addStage: 'Add a stage',
    ofTheRest: 'of the rest',
    stagesHint: '% or $ on each stage — a % is of what is left after the $ stages',
    deposit: 'Deposit',
    flatOnApproval: 'flat, on approval',
    pctOnApproval: (dollars) => `on approval — ${dollars}`,
    depositHint: 'Invoiced the moment the quote is signed.',
    theRest: 'The rest',
    rest: {
        last: 'in one invoice when the whole job is done',
        spread: 'split across the visits, as each is done',
        lastHint: 'The balance follows when the last visit on the plan is completed.',
        spreadHint: 'What is left after the deposit is shared across the visits and invoiced as each is finished.',
    },
    tmUsed: 'what was actually used —',
    tm: {
        job_completion: 'when the job is closed out',
        visit_completion: 'after each visit',
        period: 'every period',
    },
    tmHint: 'Hours actually worked, materials actually used and wasted. The quoted figure is an estimate, not a price.',
    ceiling: 'Ceiling',
    notToExceed: 'Not to exceed',
    remove: 'Remove',
    ceilingHint: 'Anything past this needs a change order. The customer sees the ceiling, never your costs.',
    addCeiling: '+ Add a ceiling',
    addCeilingHint: 'Optional. Most customers want to know the most they can be charged.',
};
/**
 * `A rental that starts Apr 6 pays 25 of April’s 30 days. The last invoice
 * takes whatever is left, so the total always matches.` — the drawn example's
 * numbers, from the plan's own window.
 */
export function prorateHint(window) {
    const tail = 'The last invoice takes whatever is left, so the total always matches.';
    if (!window || window.from.endsWith('-01'))
        return tail;
    const days = daysInMonthOf(window.from);
    const covered = days - Number(window.from.slice(8, 10)) + 1;
    return `A rental that starts ${monDay(window.from)} pays ${covered} of ${monthName(window.from)}’s `
        + `${days} days. ${tail}`;
}
/** `April – October` · `Apr 6 – Jul 5`: the For row's window, before the comma. */
export function forWindow(window) {
    return windowSaid(window).replace(' to ', ' – ');
}
/**
 * What the words of one plan need from its quote (§7.6): the visits by name,
 * how many trips it bills (the same count the proposal says) and how many
 * periods its price spreads over.
 */
export function planWordsContext(plan, visits) {
    const occurrences = plan.visitIds
        .map((id) => visits.find((visit) => visit.id === id))
        .filter((visit) => visit !== undefined && !visit.optional && !visit.declined && !visit.openEnded)
        .reduce((sum, visit) => sum + Math.max(1, visit.occurrences), 0);
    const byPeriod = plan.kind === 'period' || (plan.kind === 'time_materials' && plan.tmTrigger === 'period');
    const prorate = prorateTerms(plan);
    const periods = prorate ? prorate.periods
        : byPeriod ? plan.periodCount ?? (plan.invoices.length || null) : null;
    return { visits, occurrences, periods };
}
/**
 * The phone card's foot (Board 16 cell 1): `Next invoice Apr 1 · $321.43`,
 * or the event it waits for — `Next invoice when Spring cleanup is done`.
 */
export function nextInvoiceSaid(plan, ctx = {}) {
    const first = plan.invoices[0];
    if (!first)
        return null;
    /*
     * A completion plan bills when the work is done, not on the day it is
     * planned for (Board 16 cell 1): its date is a forecast, the event is the rule.
     */
    if (plan.kind === 'completion' && first.role === 'visit') {
        return BILLING_TAB.nextInvoice(`when ${first.description} is done`);
    }
    if (first.on)
        return BILLING_TAB.nextInvoice(`${monDay(first.on)} · ${usd(first.amount)}`);
    const row = invoiceRow(plan, first, ctx);
    if (row.tone === 'approval')
        return BILLING_TAB.nextInvoice(`on approval · ${usd(first.amount)}`);
    const event = row.invoiced.endsWith(' done') ? row.invoiced.slice(0, -' done'.length) : null;
    if (event && event !== 'Job' && event !== 'Visit')
        return BILLING_TAB.nextInvoice(`when ${event} is done`);
    if (first.role === 'visit')
        return BILLING_TAB.nextInvoice(`when ${first.description} is done`);
    return BILLING_TAB.nextInvoice('when the job is done');
}
//# sourceMappingURL=billing-words.js.map